import React, { useEffect, useState } from "react";
import { CalendarDays, Plus, RefreshCw, Trash2, Pencil, Download, AlertCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Modal, Badge } from "@/components/ui-kit";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";

/** Iter v34f (#2) — Admin manage Indonesian national holidays. */
export default function LiburNasional() {
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: "", date: "", name: "", is_cuti_bersama: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/national-holidays?year=${year}`);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  const fetchAuto = async () => {
    if (!window.confirm(`Tarik daftar libur nasional ${year} dari dayoffapi.vercel.app? Entri manual akan dipertahankan.`)) return;
    setFetching(true);
    try {
      const r = await api.post(`/national-holidays/fetch?year=${year}`);
      toast.success(`Sukses — ${r.data.added_from_api} hari libur disinkronkan (total: ${r.data.total})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sync dari API");
    } finally { setFetching(false); }
  };

  const openNew = () => { setForm({ id: "", date: "", name: "", is_cuti_bersama: false }); setOpen(true); };
  const openEdit = (r) => { setForm({ id: r.id, date: r.date, name: r.name, is_cuti_bersama: !!r.is_cuti_bersama }); setOpen(true); };

  const submit = async () => {
    if (!form.date || !form.name.trim()) return toast.error("Tanggal & nama wajib");
    try {
      if (form.id) {
        await api.patch(`/national-holidays/${form.id}`, { date: form.date, name: form.name.trim(), is_cuti_bersama: form.is_cuti_bersama });
      } else {
        await api.post(`/national-holidays`, { date: form.date, name: form.name.trim(), is_cuti_bersama: form.is_cuti_bersama });
      }
      toast.success("Disimpan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus libur ini?")) return;
    try {
      await api.delete(`/national-holidays/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  return (
    <div className="space-y-6" data-testid="libur-nasional-page">
      <PageHeader overline="HR · Kalender" title="Libur Nasional Indonesia"
        description="Daftar hari libur nasional & cuti bersama. Auto-sync dari dayoffapi.vercel.app atau input manual." />

      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <Field label="Tahun" className="!mb-0">
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())} className="w-32" data-testid="libur-year" />
        </Field>
        <Button variant="primary" onClick={fetchAuto} disabled={fetching} data-testid="libur-fetch-api">
          <Sparkles className="h-3.5 w-3.5" /> {fetching ? "Mengambil..." : `Sync dari dayoffapi (${year})`}
        </Button>
        <Button variant="outline" onClick={openNew} data-testid="libur-add">
          <Plus className="h-3.5 w-3.5" /> Tambah Manual
        </Button>
        <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
        <div className="text-[11px] text-stone-500 ml-auto"><AlertCircle className="h-3 w-3 inline" /> Entri manual tidak akan terhapus oleh sync API.</div>
      </Card>

      <Card className="p-0 overflow-x-auto" data-testid="libur-table">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Tanggal</th>
              <th className="text-left px-3 py-2.5">Hari</th>
              <th className="text-left px-3 py-2.5">Keterangan</th>
              <th className="text-center px-3 py-2.5">Jenis</th>
              <th className="text-center px-3 py-2.5">Sumber</th>
              <th className="text-center px-3 py-2.5">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={6} className="px-3 py-12 text-center text-stone-400">Memuat...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-12 text-center text-stone-400">Belum ada libur untuk tahun {year}. Klik &quot;Sync dari dayoffapi&quot; untuk auto-isi.</td></tr>}
            {rows.map((r) => {
              const d = new Date(r.date);
              const dayName = d.toLocaleDateString("id-ID", { weekday: "long" });
              const dateLabel = d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
              return (
                <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`libur-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-mono text-stone-800">{dateLabel}</td>
                  <td className="px-3 py-2.5 text-stone-600">{dayName}</td>
                  <td className="px-3 py-2.5">{r.name}</td>
                  <td className="px-3 py-2.5 text-center">
                    {r.is_cuti_bersama ? <Badge tone="amber">Cuti Bersama</Badge> : <Badge tone="emerald">Libur Nasional</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge tone={r.source === "api" ? "stone" : "blue"}>{r.source === "api" ? "API" : "Manual"}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-center whitespace-nowrap">
                    <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" data-testid={`libur-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`libur-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Libur" : "Tambah Libur Nasional / Cuti Bersama"} size="sm">
        <div className="space-y-3" data-testid="libur-form">
          <Field label="Tanggal *"><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="libur-form-date" /></Field>
          <Field label="Keterangan *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="contoh: Idul Fitri" data-testid="libur-form-name" /></Field>
          <label className="inline-flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_cuti_bersama} onChange={(e) => setForm({ ...form, is_cuti_bersama: e.target.checked })} className="h-4 w-4 accent-terra-500" data-testid="libur-form-cb" />
            Ini adalah <b>Cuti Bersama</b> (bukan libur nasional standar)
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button variant="primary" onClick={submit} data-testid="libur-form-save">Simpan</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
